import { ComponentProps } from 'react';
import { SMSDataV4 } from '@frontend/api-sms-data';
import { useCustomContactFormSlidePanel } from '@frontend/create-contact-panel';
import { useTranslation } from '@frontend/i18n';
import { useContactPanelShallowStore } from '@frontend/shared';
import { InboxPrefixes } from '@frontend/tracking-prefixes';
import { Button, PopoverMenu, PopoverMenuItem, usePopoverMenu } from '@frontend/design-system';
import { ContactListItem } from './contact-list-item';
import { buildCreateContactContext } from './utils/create-contact-helper';

type AssociatedContactsListItemProps = Omit<ComponentProps<typeof ContactListItem>, 'children'> & {
  onSelect?: () => void;
  onSetPrimaryContact?: () => void;
  disableRowInteraction?: boolean;
  phoneNumber?: string;
};

/**
 * `AssociatedContactsListItem` is a component that displays a single contact in the associated contacts list.
 * If the `person` prop is provided, it will be used to render the contact. Otherwise, the component will fetch
 * the contact using the `personId` prop.
 *
 * @param onSelect - Callback function to be called when the contact is selected (e.g., clicked).
 * @param onSetPrimaryContact - Callback function to be called when the user wants to set this contact as the primary contact.
 * @param locationId - The ID of the location associated with the person.
 * @param isPrimary - Whether this contact is currently set as the primary contact.
 * @param personId - The ID of the person to display.
 */
export const AssociatedContactsListItem = ({
  onSetPrimaryContact,
  onSelect,
  disableRowInteraction = false,
  locationId,
  isPrimary,
  personId,
  phoneNumber,
  ...rest
}: AssociatedContactsListItemProps) => {
  const { t } = useTranslation('thread-header');

  const { getMenuProps, getItemProps, getTriggerProps } = usePopoverMenu({
    placement: 'bottom-end',
  });

  const { setPersonId } = useContactPanelShallowStore('setPersonId');
  const { setShow } = useCustomContactFormSlidePanel();
  const { updateUnifiedThreadsWithNewPerson } = SMSDataV4._QueryUpdaters.useUnifiedThreadUpdater();

  return (
    <>
      <ContactListItem
        locationId={locationId}
        isPrimary={isPrimary}
        personId={personId}
        disableInteraction={disableRowInteraction}
        {...rest}
        trackingId={`${InboxPrefixes.Thread}-associated-contact-focus-contact`}
        onMouseDown={
          onSelect
            ? () => {
                onSelect();
              }
            : undefined
        }
      >
        <div
          css={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'end',
          }}
        >
          {personId ? (
            <Button
              iconName='user-small'
              variant='secondary'
              onClick={() => {
                setPersonId(personId, true, locationId);
              }}
              onMouseDown={(e) => {
                // Prevent the ContactListItem onMouseDown from being triggered
                e.stopPropagation();
              }}
              label={t('Set')}
              size='small'
              trackingId={`${InboxPrefixes.Thread}-associated-contact-view-profile`}
            />
          ) : (
            <Button
              iconName='assign-user'
              variant='secondary'
              onClick={() => {
                setShow({
                  show: true,
                  context: buildCreateContactContext({
                    firstName: rest.person?.firstName,
                    lastName: rest.person?.lastName,
                    phoneNumber,
                    locationId,
                    onSave: (res, req) => {
                      const allPersonPhones = [req.phone_mobile, req.phone_home, phoneNumber].filter(
                        (phone): phone is string => !!phone
                      );
                      updateUnifiedThreadsWithNewPerson({
                        personPhones: allPersonPhones,
                        firstName: req.first_name,
                        lastName: req.last_name,
                        preferredName: req.preferred_name ?? '',
                        personId: res.data.person_id,
                        locationId,
                        onlyEmptyPersons: true,
                      });
                    },
                  }),
                });
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
              label={t('Add')}
              size='small'
              trackingId={`${InboxPrefixes.Thread}-associated-contact-add-contact`}
            />
          )}
          {onSetPrimaryContact && !!personId && (
            <Button
              iconName='more-small'
              variant='secondary'
              {...getTriggerProps()}
              onMouseDown={(e) => {
                // Prevent the ContactListItem onMouseDown from being triggered
                e.stopPropagation();
              }}
              label={t('Open Associated Contact Actions')}
              size='small'
              disabled={isPrimary}
              css={
                isPrimary && {
                  backgroundColor: 'transparent',
                }
              }
              trackingId={`${InboxPrefixes.Thread}-associated-contact-more-menu`}
              data-value={personId}
            />
          )}
        </div>
      </ContactListItem>
      {onSetPrimaryContact && (
        <PopoverMenu {...getMenuProps()}>
          <PopoverMenuItem
            {...getItemProps({
              index: 0,
              onClick: (e) => {
                onSetPrimaryContact();
                e.stopPropagation();
              },
              trackingId: `${InboxPrefixes.Thread}-associated-contact-set-primary`,
            })}
            css={{
              cursor: 'pointer',
            }}
          >
            {t('Make Phone Owner')}
          </PopoverMenuItem>
        </PopoverMenu>
      )}
    </>
  );
};
